import L from "leaflet";

// ── Colormaps ─────────────────────────────────────────────────────────────────

const COLORMAPS = [
  { value: "blues_r",        label: "Blues - White"   },
  { value: "viridis",        label: "Viridis"         },
  { value: "turbo",          label: "Turbo"           },
  { value: "magma",          label: "Magma"           },
  { value: "rdylbu_r",       label: "Red-Blue"        },
  { value: "gray",           label: "Grayscale"       },
  { value: "pastel_rainbow", label: "Pastel Rainbow"  },
  { value: "earth",          label: "Earth"           },
  { value: "polar",          label: "Polar"           },
  { value: "copper",         label: "Copper"          },
  { value: "solid",          label: "Custom"          },
];

const COLORMAP_STOPS = {
  blues_r:        [[0,[8,48,107]],[0.4,[33,113,181]],[0.7,[189,215,231]],[1,[255,255,255]]],
  viridis:        [[0,[68,1,84]],[0.25,[59,82,139]],[0.5,[33,145,140]],[0.75,[94,201,98]],[1,[253,231,37]]],
  turbo:          [[0,[35,23,27]],[0.25,[68,118,247]],[0.5,[66,211,164]],[0.75,[212,239,7]],[1,[144,12,0]]],
  magma:          [[0,[0,0,4]],[0.25,[81,18,124]],[0.5,[190,60,80]],[0.75,[251,176,100]],[1,[252,253,191]]],
  rdylbu_r:       [[0,[49,54,149]],[0.25,[171,217,233]],[0.5,[255,255,191]],[0.75,[253,174,97]],[1,[165,0,38]]],
  gray:           [[0,[0,0,0]],[1,[255,255,255]]],
  pastel_rainbow: [[0,[180,120,200]],[0.17,[130,150,230]],[0.33,[100,200,200]],[0.5,[130,210,150]],[0.67,[220,220,120]],[0.83,[240,170,120]],[1,[230,140,150]]],
  earth:          [[0,[15,40,110]],[0.2,[25,100,150]],[0.38,[200,170,90]],[0.5,[100,160,80]],[0.7,[140,110,65]],[0.85,[170,155,135]],[1,[230,230,225]]],
  polar:          [[0,[30,60,180]],[0.25,[120,170,220]],[0.5,[255,255,255]],[0.75,[220,130,100]],[1,[180,30,30]]],
  copper:         [[0,[0,0,0]],[0.4,[85,35,5]],[0.75,[175,95,40]],[1,[255,175,85]]],
};

function getColormapColor(t, name) {
  const stops = COLORMAP_STOPS[name] || COLORMAP_STOPS.blues_r;
  t = Math.max(0, Math.min(1, t));
  let lo = stops[0], hi = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (t >= stops[i][0] && t <= stops[i + 1][0]) { lo = stops[i]; hi = stops[i + 1]; break; }
  }
  const span = hi[0] - lo[0];
  const f = span < 1e-10 ? 0 : (t - lo[0]) / span;
  return [0, 1, 2].map(j => Math.round(lo[1][j] + f * (hi[1][j] - lo[1][j])));
}

function colormapGradientCss(name) {
  if (name === "solid") return "linear-gradient(to right,#dc3232,#38bdf8,#22c55e)";
  const stops = COLORMAP_STOPS[name];
  if (!stops) return "#888";
  return `linear-gradient(to right,${stops.map(([t,[r,g,b]]) => `rgb(${r},${g},${b}) ${(t*100).toFixed(0)}%`).join(",")})`;
}

function rgbToHex(rgb) { return "#" + rgb.map(v => v.toString(16).padStart(2,"0")).join(""); }
function hexToRgb(hex) { const s = hex.replace("#",""); return [0,2,4].map(i => parseInt(s.slice(i,i+2),16)); }
function esc(v) { return String(v??"").replaceAll("&","&amp;").replaceAll("<","&lt;").replaceAll(">","&gt;").replaceAll('"',"&quot;"); }
function uid() { return Math.random().toString(36).slice(2)+Date.now().toString(36); }

function barLabelLeft(pct) {
  if (pct <= 0) return "0%";
  if (pct >= 100) return "100%";
  return `${pct.toFixed(2)}%`;
}
function barLabelTransform(pct) {
  if (pct <= 0) return "none";
  if (pct >= 100) return "translateX(-100%)";
  return "translateX(-50%)";
}

const DEFAULT_SETTINGS = () => ({
  displayUnit: "ft",
  colormapName: "blues_r",
  rescaleMin: null,
  rescaleMax: null,
  underflowColor: [45,55,72],
  overflowColor: [45,55,72],
  underflowTransparent: false,
  overflowTransparent: false,
  tileOpacity: 1.0,
  hillshadeOverlay: true,
  customBins: 3,
  customColors: [[220,50,50],[56,189,248],[34,197,94]],
  customDividers: null,
  customTransparent: [],
  hillshadeAzimuth: 315,
  hillshadeAltitude: 45,
  tileZFactor: 1.0,
  tileGamma: 1.0,
  tileSaturation: 1.0,
  tileContrast: 1.0,
  tileResampling: "bilinear",
});

// ── Module state ──────────────────────────────────────────────────────────────

let app;
let container = null;
let activeMarinaCode = "";
let catalog = [];
let available = [];
let selectedId = null;
let selectedIds = new Set();
let visibleIds = new Set();
let loadingCatalog = false;
let loadingAvailable = false;
let savePending = false;
let addPickerOpen = false;
let cmPickerOpen = false; // colormap picker dropdown

const surfaceRuntimes = new Map();
const buildPolls = new Map();
let dragSrcId = null;
let closeCmDropdown = null;
let lastMergedHistData = null; // merged histogram across all selected surfaces (multi-select mode)
let rebuildOpen = false;

// ── Colormap ID registry (client-side dedup cache) ────────────────────────────
const cmIdCache  = new Map(); // cmJson → id
const cmIdFlight = new Map(); // cmJson → Promise<id>

async function registerColormap(cmJson) {
  if (cmIdCache.has(cmJson)) return cmIdCache.get(cmJson);
  if (cmIdFlight.has(cmJson)) return cmIdFlight.get(cmJson);
  const p = fetch("/api/qimera/colormap", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ colormap: cmJson }),
  }).then(r => r.json()).then(d => {
    cmIdFlight.delete(cmJson);
    if (d?.id) { cmIdCache.set(cmJson, d.id); return d.id; }
    return null;
  }).catch(() => { cmIdFlight.delete(cmJson); return null; });
  cmIdFlight.set(cmJson, p);
  return p;
}

const surfaceTaskStatus = (msg, pct, running = true) => app?.events?.emit?.("task:status", {
  source: "surfaces", label: "Surfaces", message: msg || "", progress: pct ?? null,
  status: running ? "running" : "idle",
});
const surfaceTaskIdle = () => surfaceTaskStatus("", null, false);

function getSelected() { return catalog.find(s => s.id === selectedId) || null; }

function applyToAllSelected(fn) {
  for (const id of selectedIds) {
    const s = catalog.find(c => c.id === id);
    if (s) fn(s);
  }
}

// ── S3 persistence ────────────────────────────────────────────────────────────

async function loadCatalog() {
  if (!activeMarinaCode) { catalog = []; render(); return; }
  loadingCatalog = true; render();
  try {
    const r = await fetch(`/api/surfaces/catalog?marina=${encodeURIComponent(activeMarinaCode)}`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    catalog = Array.isArray(d.catalog) ? d.catalog : [];
    catalog.forEach(s => {
      if (!s.settings) s.settings = DEFAULT_SETTINGS();
      const def = DEFAULT_SETTINGS();
      // back-compat: fill any fields missing from older catalog entries
      for (const k of Object.keys(def)) {
        if (s.settings[k] === undefined) s.settings[k] = def[k];
      }
    });
    catalog.forEach(s => { if (!s.rasterReady) startBuildPoll(s.id); });
    const validIds = new Set(catalog.map(s => s.id));
    for (const id of [...selectedIds]) if (!validIds.has(id)) selectedIds.delete(id);
    if (selectedId && !validIds.has(selectedId)) selectedId = [...selectedIds][0] ?? null;
  } catch {}
  loadingCatalog = false; render();
}

async function saveCatalog() {
  if (!activeMarinaCode) return;
  savePending = true; render();
  try {
    await fetch("/api/surfaces/catalog", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ marina: activeMarinaCode, catalog }),
    });
  } catch {}
  savePending = false; render();
}

async function loadAvailable() {
  loadingAvailable = true; render();
  try {
    const r = await fetch("/api/surfaces/available");
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    available = Array.isArray(d.surfaces) ? d.surfaces : [];
  } catch {}
  loadingAvailable = false; render();
}

// ── Build polling ─────────────────────────────────────────────────────────────

function startBuildPoll(id) {
  if (buildPolls.has(id)) return;
  const poll = async () => {
    const s = catalog.find(c => c.id === id);
    if (!s || s.rasterReady) {
      buildPolls.delete(id);
      if (!buildPolls.size) surfaceTaskIdle();
      render(); return;
    }
    try {
      const r = await fetch(`/api/surfaces/build-status?surfaceId=${encodeURIComponent(s.id)}`);
      const data = await r.json();
      s.buildJob = data.job || null;
      if (data.rasterReady) {
        s.rasterReady = true;
        s.rasterPath = data.rasterPath;
        buildPolls.delete(id);
        saveCatalog();
        if (visibleIds.has(id)) { hideSurface(id); showSurface(id); }
        render();
        app?.ui?.toast?.(`Surface ready: ${s.name}`);
        app?.log?.("surface.build.complete", { surfaceId: id, name: s.name, rasterPath: data.rasterPath });
        if (!buildPolls.size) surfaceTaskIdle();
        return;
      }
      if (data.job?.status === "error") {
        buildPolls.delete(id);
        render();
        app?.log?.("surface.build.error", { surfaceId: id, name: s.name, message: data.job?.message });
        if (!buildPolls.size) surfaceTaskIdle();
        return;
      }
      // Still running — update status bar with this job's progress
      surfaceTaskStatus(data.job?.message || "Building raster…", data.job?.progressPct ?? null);
    } catch {}
    buildPolls.set(id, setTimeout(poll, 3000));
    render();
  };
  buildPolls.set(id, setTimeout(poll, 1500));
}

function stopBuildPoll(id) {
  const t = buildPolls.get(id);
  if (t != null) clearTimeout(t);
  buildPolls.delete(id);
}

// ── Map layer management ──────────────────────────────────────────────────────

function getSurfaceRuntime(id) {
  if (!surfaceRuntimes.has(id)) {
    const paneName = `surfPane_${id}`;
    const hillPaneName = `surfHillPane_${id}`;
    const map = app.map.instance;
    let paneEl, hillPaneEl;
    try { paneEl = map.createPane(paneName); } catch { paneEl = map.getPane(paneName); }
    try { hillPaneEl = map.createPane(hillPaneName); } catch { hillPaneEl = map.getPane(hillPaneName); }
    const zBase = 810 + (catalog.length - catalog.findIndex(s => s.id === id)) * 2;
    paneEl.style.zIndex = String(zBase);
    hillPaneEl.style.zIndex = String(zBase + 1);
    hillPaneEl.style.mixBlendMode = "multiply";
    surfaceRuntimes.set(id, { tileLayer: null, hillshadeLayer: null, colormapId: null, showPending: false, paneEl, hillPaneEl, paneName, hillPaneName, surfId: id, histData: null, histFetching: false, tileProgressState: { pending: 0, total: 0, timer: null } });
  }
  return surfaceRuntimes.get(id);
}

function ensureCustomDividers(s, lo, hi) {
  const st = s.settings;
  const defaults = [[220,50,50],[56,189,248],[34,197,94],[251,191,36],[167,139,250]];
  while (st.customColors.length < st.customBins) st.customColors.push(defaults[st.customColors.length % defaults.length]);
  while (st.customTransparent.length < st.customBins) st.customTransparent.push(false);
  if (!st.customDividers || st.customDividers.length !== st.customBins - 1) {
    st.customDividers = Array.from({length: st.customBins - 1}, (_, i) => lo + (hi - lo) * (i + 1) / st.customBins);
  }
  return [...st.customDividers].sort((a, b) => a - b);
}

function buildIntervalColormap(s) {
  const { colormapName, underflowColor, overflowColor, underflowTransparent, overflowTransparent } = s.settings;
  if (colormapName === "solid") {
    const rt = surfaceRuntimes.get(s.id);
    const hd = rt?.histData || null;
    const lo = hd?.[1]?.[0] ?? (s.settings.rescaleMin ?? -50);
    const hi = hd?.[1]?.[hd[1].length-1] ?? (s.settings.rescaleMax ?? 0);
    const divs = ensureCustomDividers(s, lo, hi);
    const far = (Math.abs(hi - lo) + 1) * 1000;
    const a = (i) => (s.settings.customTransparent[i] ? 0 : 255);
    const col = (i) => [...(s.settings.customColors[Math.min(i, s.settings.customColors.length-1)] || [128,128,128]), a(i)];
    if (divs.length === 0) return JSON.stringify([[[-far, far], col(0)]]);
    const entries = [];
    entries.push([[-far, divs[0]], col(0)]);
    for (let i = 0; i < divs.length - 1; i++) entries.push([[divs[i], divs[i+1]], col(i+1)]);
    entries.push([[divs[divs.length-1], far], col(divs.length)]);
    return JSON.stringify(entries);
  }
  const rMin = s.settings.rescaleMin ?? -50;
  const rMax = s.settings.rescaleMax ?? 0;
  const span = rMax - rMin;
  if (Math.abs(span) < 0.001) {
    const [r,g,b] = getColormapColor(0.5, colormapName);
    return JSON.stringify([[[rMin-1, rMax+1], [r,g,b,255]]]);
  }
  const pad = (Math.abs(span)+1)*100;
  const steps = 256;
  const entries = [];
  entries.push([[rMin-pad, rMin], [...underflowColor, underflowTransparent ? 0 : 255]]);
  for (let i = 0; i < steps; i++) {
    const t0 = i/steps, t1 = (i+1)/steps;
    const [r,g,b] = getColormapColor((t0+t1)/2, colormapName);
    entries.push([[rMin+t0*span, rMin+t1*span], [r,g,b,255]]);
  }
  entries.push([[rMax, rMax+pad], [...overflowColor, overflowTransparent ? 0 : 255]]);
  return JSON.stringify(entries);
}

function buildTileUrl(s, cmId = null) {
  const raster = encodeURIComponent(s.rasterPath);
  const cmParam = cmId
    ? `colormap_id=${encodeURIComponent(cmId)}`
    : `colormap=${encodeURIComponent(buildIntervalColormap(s))}`;
  return `/api/qimera/cog-tiles/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${raster}&${cmParam}&resampling=${s.settings.tileResampling||"bilinear"}`;
}

function buildHillshadeUrl(s) {
  const params = encodeURIComponent(JSON.stringify({ azimuth: s.settings.hillshadeAzimuth, angle_altitude: s.settings.hillshadeAltitude, z_factor: s.settings.tileZFactor ?? 1.0 }));
  return `/api/qimera/cog-tiles/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodeURIComponent(s.rasterPath)}&algorithm=hillshade&algorithm_params=${params}`;
}

// Canvas-composite hillshade layer: uses the colormap tile's alpha channel as a mask so
// underflow/overflow transparent regions are also transparent in the hillshade layer.
function buildMaskedHillshadeLayer(hillshadeUrlTmpl, colormapUrlTmpl, options) {
  const layer = L.gridLayer(options);
  layer.createTile = function(coords, done) {
    const size = this.getTileSize();
    const canvas = document.createElement("canvas");
    canvas.width = size.x; canvas.height = size.y;
    const ctx = canvas.getContext("2d");
    const hUrl = L.Util.template(hillshadeUrlTmpl, coords);
    const cUrl = L.Util.template(colormapUrlTmpl, coords);
    let loaded = 0;
    const hs = new Image(), cm = new Image();
    const onLoad = () => {
      if (++loaded < 2) return;
      ctx.drawImage(hs, 0, 0);
      ctx.globalCompositeOperation = "destination-in";
      ctx.drawImage(cm, 0, 0);
      done(null, canvas);
    };
    const onErr = (e) => done(e, canvas);
    hs.crossOrigin = cm.crossOrigin = "anonymous";
    hs.onload = cm.onload = onLoad;
    hs.onerror = cm.onerror = onErr;
    hs.src = hUrl; cm.src = cUrl;
    return canvas;
  };
  return layer;
}

// Create a hillshade layer — masked when underflow/overflow transparency is active.
function createHillshadeLayer(s, rt, map, cmId = null) {
  const opts = { tileSize: 256, opacity: s.settings.tileOpacity, maxZoom: 22, pane: rt.hillPaneName };
  const needsMask = s.settings.underflowTransparent || s.settings.overflowTransparent;
  if (needsMask && s.settings.rescaleMin != null && s.settings.rescaleMax != null) {
    return buildMaskedHillshadeLayer(buildHillshadeUrl(s), buildTileUrl(s, cmId), opts).addTo(map);
  }
  return L.tileLayer(buildHillshadeUrl(s), opts).addTo(map);
}

function buildPaneFilter(s) {
  const parts = [];
  const gamma = s.settings.tileGamma ?? 1.0;
  const sat   = s.settings.tileSaturation ?? 1.0;
  const con   = s.settings.tileContrast ?? 1.0;
  if (gamma !== 1.0) parts.push(`brightness(${gamma.toFixed(2)})`);
  if (sat   !== 1.0) parts.push(`saturate(${sat.toFixed(2)})`);
  if (con   !== 1.0) parts.push(`contrast(${con.toFixed(2)})`);
  return parts.join(" ");
}

function bindTileProgress(rt, layer) {
  if (!layer) return;
  layer.on("loading", () => {
    if (rt.tileProgressState.timer) clearTimeout(rt.tileProgressState.timer);
    rt.tileProgressState = { pending:0, total:0, timer:null };
    updateProgressUI(rt);
  });
  layer.on("tileloadstart", () => { rt.tileProgressState.total++; rt.tileProgressState.pending++; updateProgressUI(rt); });
  layer.on("tileload tileerror", () => {
    rt.tileProgressState.pending = Math.max(0, rt.tileProgressState.pending-1);
    updateProgressUI(rt);
    if (rt.tileProgressState.pending <= 0 && rt.tileProgressState.total > 0) {
      rt.tileProgressState.timer = setTimeout(() => { rt.tileProgressState = { pending:0, total:0, timer:null }; updateProgressUI(rt); }, 800);
    }
  });
}

function updateProgressUI(rt) {
  if (!container) return;
  const { pending, total } = rt.tileProgressState;
  const bar = container.querySelector(`[data-surf-tile-bar="${rt.surfId}"]`);
  const txt = container.querySelector(`[data-surf-tile-txt="${rt.surfId}"]`);
  const wrap = container.querySelector(`[data-surf-tile-wrap="${rt.surfId}"]`);
  if (!wrap) return;
  if (!total) { wrap.style.display = "none"; return; }
  const loaded = total - pending;
  const pct = total > 0 ? Math.round((loaded/total)*100) : 0;
  wrap.style.display = "block";
  if (bar) bar.style.width = `${pct}%`;
  if (txt) txt.textContent = `${loaded}/${total} tiles`;
}

async function showSurface(id) {
  const s = catalog.find(c => c.id === id);
  if (!s || !s.rasterReady || !s.rasterPath) return;
  const rt = getSurfaceRuntime(id);
  if (rt.showPending) return;
  rt.showPending = true;
  try {
    const map = app.map.instance;
    if (!rt.tileLayer) {
      const cmJson = buildIntervalColormap(s);
      const cmId = await registerColormap(cmJson);
      rt.colormapId = cmId;
      rt.tileLayer = L.tileLayer(buildTileUrl(s, cmId), { tileSize:256, opacity:s.settings.tileOpacity, maxZoom:22, pane:rt.paneName }).addTo(map);
      bindTileProgress(rt, rt.tileLayer);
    }
    if (s.settings.hillshadeOverlay && !rt.hillshadeLayer) {
      rt.hillshadeLayer = createHillshadeLayer(s, rt, map, rt.colormapId ?? null);
      bindTileProgress(rt, rt.hillshadeLayer);
    }
    rt.paneEl.style.filter = buildPaneFilter(s);
    visibleIds.add(id);
    if (!rt.histData && !rt.histFetching) fetchHistogram(id);
  } finally {
    rt.showPending = false;
  }
}

function hideSurface(id) {
  const rt = surfaceRuntimes.get(id);
  if (!rt) return;
  if (rt.tileLayer) { try { rt.tileLayer.remove(); } catch {} rt.tileLayer = null; }
  if (rt.hillshadeLayer) { try { rt.hillshadeLayer.remove(); } catch {} rt.hillshadeLayer = null; }
  rt.colormapId = null;
  visibleIds.delete(id);
}

async function refreshSurface(id) {
  if (!visibleIds.has(id)) return;
  hideSurface(id);
  await showSurface(id);
}

async function fetchHistogram(id) {
  const s = catalog.find(c => c.id === id);
  if (!s || !s.rasterPath) return;
  const rt = getSurfaceRuntime(id);
  if (rt.histFetching) return;
  rt.histFetching = true;
  try {
    const r = await fetch(`/api/qimera/cog-tiles/statistics?url=${encodeURIComponent(s.rasterPath)}&histogram_bins=256`);
    if (!r.ok) return;
    const data = await r.json();
    const band = data.b1 || Object.values(data)[0];
    if (band?.histogram) {
      rt.histData = band.histogram;
      if (s.settings.rescaleMin === null) s.settings.rescaleMin = Math.round((band.percentile_2 ?? band.min ?? -50)*10)/10;
      if (s.settings.rescaleMax === null) s.settings.rescaleMax = Math.round((band.percentile_98 ?? band.max ?? 0)*10)/10;
      refreshSurface(id);
      if (id === selectedId) render();
    }
  } catch {}
  rt.histFetching = false;
}

function reapplyZIndices() {
  catalog.forEach((s, i) => {
    const rt = surfaceRuntimes.get(s.id);
    if (!rt) return;
    const zBase = 810 + (catalog.length - i) * 2;
    rt.paneEl.style.zIndex = String(zBase);
    rt.hillPaneEl.style.zIndex = String(zBase + 1);
  });
}

// ── Histogram rendering ───────────────────────────────────────────────────────

function generateHistogramBars(counts, edges, s) {
  const { colormapName, underflowColor, overflowColor } = s.settings;
  const n = counts.length;
  const maxCount = Math.max(...counts, 1);
  const bw = 100/n;
  if (colormapName === "solid") {
    const lo = edges?.[0] ?? -50;
    const hi = edges?.[edges.length-1] ?? 0;
    const divs = ensureCustomDividers(s, lo, hi);
    return counts.map((c, i) => {
      const pct = (c/maxCount)*100;
      const binCenter = ((edges[i]??0)+(edges[i+1]??0))/2;
      let binIdx = divs.findIndex(d => binCenter < d);
      if (binIdx === -1) binIdx = divs.length;
      const [r,g,b] = s.settings.customColors[Math.min(binIdx, s.settings.customColors.length-1)] || [128,128,128];
      return `<rect x="${(i*bw).toFixed(2)}%" y="${(100-pct).toFixed(2)}%" width="${bw.toFixed(2)}%" height="${pct.toFixed(2)}%" fill="rgb(${r},${g},${b})"/>`;
    }).join("");
  }
  const rMin = s.settings.rescaleMin ?? (edges?.[0] ?? -50);
  const rMax = s.settings.rescaleMax ?? (edges?.[edges.length-1] ?? 0);
  return counts.map((c, i) => {
    const pct = (c/maxCount)*100;
    const binCenter = ((edges[i]??0)+(edges[i+1]??0))/2;
    let fill;
    if (binCenter < rMin) fill = rgbToHex(underflowColor);
    else if (binCenter > rMax) fill = rgbToHex(overflowColor);
    else {
      const t = Math.abs(rMax-rMin) < 1e-10 ? 0.5 : (binCenter-rMin)/(rMax-rMin);
      const [r,g,b] = getColormapColor(t, colormapName);
      fill = `rgb(${r},${g},${b})`;
    }
    return `<rect x="${(i*bw).toFixed(2)}%" y="${(100-pct).toFixed(2)}%" width="${bw.toFixed(2)}%" height="${pct.toFixed(2)}%" fill="${fill}"/>`;
  }).join("");
}

function mergeHistograms(histDataArray) {
  const valid = histDataArray.filter(h => h?.[0]?.length && h?.[1]?.length >= 2);
  if (!valid.length) return null;
  if (valid.length === 1) return valid[0];
  const globalLo = Math.min(...valid.map(h => h[1][0]));
  const globalHi = Math.max(...valid.map(h => h[1][h[1].length - 1]));
  if (globalHi <= globalLo) return valid[0];
  const N = 256;
  const step = (globalHi - globalLo) / N;
  const mergedEdges = Array.from({ length: N + 1 }, (_, i) => globalLo + i * step);
  const mergedCounts = new Array(N).fill(0);
  for (const [counts, edges] of valid) {
    for (let i = 0; i < counts.length; i++) {
      const srcLo = edges[i], srcHi = edges[i + 1];
      const srcWidth = srcHi - srcLo;
      if (srcWidth <= 0 || counts[i] <= 0) continue;
      const firstBin = Math.max(0, Math.floor((srcLo - globalLo) / step));
      const lastBin = Math.min(N - 1, Math.ceil((srcHi - globalLo) / step));
      for (let j = firstBin; j <= lastBin; j++) {
        const overlap = Math.max(0, Math.min(srcHi, mergedEdges[j + 1]) - Math.max(srcLo, mergedEdges[j]));
        mergedCounts[j] += counts[i] * (overlap / srcWidth);
      }
    }
  }
  return [mergedCounts, mergedEdges];
}

// ── Render ────────────────────────────────────────────────────────────────────

function render() {
  if (!container) return;
  const sel = getSelected();

  const btnSty = `background:rgba(148,163,184,0.08);border:1px solid rgba(148,163,184,0.18);color:var(--text);border-radius:4px;padding:2px 9px;font-size:11px;cursor:pointer;`;
  const btnSmSty = `background:rgba(148,163,184,0.08);border:1px solid rgba(148,163,184,0.18);color:var(--text);border-radius:4px;padding:1px 7px;font-size:10px;cursor:pointer;`;
  const dangerSty = `${btnSmSty}color:#f87171;`;

  // ── Catalog rows ──────────────────────────────────────────────────────────
  let catalogRows = "";
  if (loadingCatalog) {
    catalogRows = `<div class="small" style="padding:8px 10px;color:var(--muted);">Loading…</div>`;
  } else if (!catalog.length) {
    catalogRows = `<div class="small" style="padding:8px 10px;color:var(--muted);">No surfaces yet. Click + Add to add one.</div>`;
  } else {
    catalogRows = catalog.map((s, idx) => {
      const vis = visibleIds.has(s.id);
      const isPrimary = s.id === selectedId;
      const isSel = selectedIds.has(s.id);
      const isBuilding = !s.rasterReady;
      const buildError = s.buildJob?.status === "error";
      const topBorder = idx > 0 ? "border-top:1px solid rgba(148,163,184,0.1);" : "";
      const bg = isPrimary ? "background:rgba(56,189,248,0.10);" : isSel ? "background:rgba(56,189,248,0.05);" : "";
      const rt = surfaceRuntimes.get(s.id);
      const { pending, total } = rt?.tileProgressState || { pending:0, total:0 };
      const opPct = Math.round((s.settings?.tileOpacity ?? 1.0) * 100);
      const cmLabel = COLORMAPS.find(c => c.value === s.settings?.colormapName)?.label || s.settings?.colormapName || "";
      const cmGrad = colormapGradientCss(s.settings?.colormapName || "blues_r");

      let statusBadge = "";
      if (buildError) statusBadge = `<span style="font-size:9px;color:#f87171;margin-left:4px;">build error</span>`;
      else if (isBuilding) statusBadge = `<span style="font-size:9px;color:#fbbf24;margin-left:4px;">${esc(s.buildJob?.message||"Building…")} ${s.buildJob?.progressPct > 0 ? s.buildJob.progressPct+"%" : ""}</span>`;

      const tileProgressBar = total > 0
        ? `<div data-surf-tile-wrap="${esc(s.id)}" style="margin:2px 10px 0;height:3px;border-radius:2px;background:rgba(148,163,184,0.15);overflow:hidden;"><div data-surf-tile-bar="${esc(s.id)}" style="height:100%;border-radius:2px;background:linear-gradient(90deg,#38bdf8,#22c55e);width:0%;transition:width 0.15s;"></div></div><div data-surf-tile-txt="${esc(s.id)}" class="small" style="font-size:9px;color:var(--muted);text-align:right;padding:0 10px 2px;"></div>`
        : `<div data-surf-tile-wrap="${esc(s.id)}" style="display:none;height:3px;"></div><div data-surf-tile-txt="${esc(s.id)}"></div>`;

      return `<div data-surf-row="${esc(s.id)}" draggable="true" style="${topBorder}${bg}padding:6px 10px 4px;">
        <div style="display:flex;align-items:center;gap:6px;">
          <input type="checkbox" data-surf-vis="${esc(s.id)}" ${vis?"checked":""} ${isBuilding?"disabled":""} style="flex-shrink:0;" />
          <span data-surf-select="${esc(s.id)}" class="small" style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;cursor:pointer;font-weight:${isPrimary?"600":isSel?"500":"400"};color:${isSel?"var(--text)":isBuilding?"var(--muted)":"var(--text)"};" title="${esc(s.name)}">${esc(s.name)}</span>
          ${statusBadge}
          ${!isBuilding ? `
            <div style="width:40px;height:8px;border-radius:2px;background:${cmGrad};flex-shrink:0;opacity:0.8;" title="${esc(cmLabel)}"></div>
            <input type="range" data-surf-opacity="${esc(s.id)}" min="0" max="100" value="${opPct}" style="width:52px;flex-shrink:0;cursor:pointer;" title="Opacity: ${opPct}%" />
            <span data-surf-opacity-label="${esc(s.id)}" class="small" style="color:var(--muted);font-size:9px;min-width:26px;text-align:right;">${opPct}%</span>
          ` : ""}
          <button style="${btnSmSty}" data-surf-rename="${esc(s.id)}" title="Rename">✎</button>
          <button style="${dangerSty}" data-surf-remove="${esc(s.id)}" title="Remove">✕</button>
        </div>
        ${isBuilding && !buildError ? `<div style="margin:4px 0 2px;height:3px;border-radius:2px;background:rgba(148,163,184,0.15);overflow:hidden;"><div style="height:100%;border-radius:2px;background:linear-gradient(90deg,#fbbf24,#f59e0b);width:${s.buildJob?.progressPct??5}%;transition:width 0.5s;"></div></div>` : ""}
        ${tileProgressBar}
      </div>`;
    }).join("");
  }

  // ── Add picker ────────────────────────────────────────────────────────────
  let addPickerHtml = "";
  if (addPickerOpen) {
    if (loadingAvailable) {
      addPickerHtml = `<div style="border-top:1px solid rgba(148,163,184,0.12);padding:8px 10px;"><span class="small" style="color:var(--muted);">Loading point files…</span></div>`;
    } else if (!available.length) {
      addPickerHtml = `<div style="border-top:1px solid rgba(148,163,184,0.12);padding:8px 10px;"><span class="small" style="color:var(--muted);">No point files found in the DAM. Upload LAS/LAZ files in Image Generator → Digital Asset Manager first.</span></div>`;
    } else {
      const addedS3Keys = new Set(catalog.map(s => s.s3Key));
      const picks = available.map(av => {
        const alreadyAdded = addedS3Keys.has(av.s3Key);
        const safeKey = esc(av.s3Key);
        return `<div style="padding:5px 10px;border-top:1px solid rgba(148,163,184,0.07);">
          <div style="display:flex;align-items:center;gap:8px;">
            <span class="small" style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${esc(av.fileName)}">
              <span style="color:var(--muted);">${esc(av.marina)} —</span> ${esc(av.name)}
              ${alreadyAdded ? `<span style="font-size:9px;color:#fbbf24;margin-left:4px;">in catalog</span>` : ""}
            </span>
            <button style="${btnSmSty}" data-surf-add-pick="${esc(av.marina)}" data-surf-add-name="${esc(av.name)}" data-surf-add-s3key="${safeKey}" data-surf-add-unit="${esc(av.nativeUnit)}">Add + Build</button>
          </div>
          <div style="display:flex;align-items:center;gap:8px;margin-top:3px;padding-left:2px;">
            <span class="small" style="color:var(--muted);font-size:9px;white-space:nowrap;">Source EPSG:</span>
            <input type="text" class="surf-pick-epsg" data-surf-epsg-key="${safeKey}" placeholder="auto" style="width:56px;font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 4px;" />
            <span class="small" style="color:var(--muted);font-size:9px;white-space:nowrap;">Grid res:</span>
            <select class="surf-pick-res" data-surf-res-key="${safeKey}" style="font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 3px;">
              <option value="0.25">0.25 ${esc(av.nativeUnit)}</option>
              <option value="0.5">0.5 ${esc(av.nativeUnit)}</option>
              <option value="1" selected>1 ${esc(av.nativeUnit)}</option>
              <option value="2">2 ${esc(av.nativeUnit)}</option>
              <option value="5">5 ${esc(av.nativeUnit)}</option>
            </select>
          </div>
        </div>`;
      }).join("");
      addPickerHtml = `<div style="border-top:1px solid rgba(148,163,184,0.12);background:rgba(2,6,23,0.3);max-height:220px;overflow-y:auto;">${picks}</div>`;
    }
  }

  // ── Viz controls ──────────────────────────────────────────────────────────
  let vizHtml = "";
  if (sel && sel.rasterReady) {
    const st = sel.settings;
    const rt = surfaceRuntimes.get(sel.id);
    const isMulti = selectedIds.size > 1;
    if (isMulti) {
      const hists = [];
      for (const id of selectedIds) {
        const rt2 = surfaceRuntimes.get(id);
        if (rt2?.histData) hists.push(rt2.histData);
      }
      lastMergedHistData = mergeHistograms(hists);
    } else {
      lastMergedHistData = null;
    }
    const histData = (isMulti ? lastMergedHistData : rt?.histData) || null;
    const histReadyCount = isMulti ? [...selectedIds].filter(id => surfaceRuntimes.get(id)?.histData).length : 0;
    const histLo = histData?.[1]?.[0] ?? (st.rescaleMin ?? -50);
    const histHi = histData?.[1]?.[histData[1].length-1] ?? (st.rescaleMax ?? 0);
    const rMin = st.rescaleMin ?? histLo;
    const rMax = st.rescaleMax ?? histHi;
    const range = histHi - histLo || 1;
    const pMin = Math.max(0, Math.min(100, (rMin-histLo)/range*100));
    const pMax = Math.max(0, Math.min(100, (rMax-histLo)/range*100));

    const histSvgInner = histData
      ? generateHistogramBars(histData[0], histData[1], sel)
      : "";
    const histSvgEl = histData
      ? `<svg id="surf-hist-svg" viewBox="0 0 100 52" preserveAspectRatio="none" style="width:100%;height:52px;display:block;">${histSvgInner}</svg>`
      : `<div style="height:52px;display:flex;align-items:center;justify-content:center;"><span class="small" style="color:var(--muted);">${rt?.histFetching?"Loading…":"Histogram unavailable"}</span></div>`;

    const barDivSty = (pct) =>
      `position:absolute;top:0;height:52px;width:2px;background:rgba(255,255,255,0.9);` +
      `cursor:ew-resize;left:${pct.toFixed(2)}%;transform:translateX(-50%);pointer-events:all;box-shadow:0 0 4px rgba(0,0,0,0.6);`;
    const lblFollowSty = (pct) =>
      `position:absolute;top:55px;left:${barLabelLeft(pct)};transform:${barLabelTransform(pct)};` +
      `font-size:8px;color:#e2e8f0;white-space:nowrap;pointer-events:none;text-shadow:0 0 4px #000;`;

    // colormap dropdown
    const cmSelected = COLORMAPS.find(c => c.value === st.colormapName) || COLORMAPS[0];
    const cmGradCurrent = colormapGradientCss(cmSelected.value);
    const cmDropdownHtml = `<div style="position:relative;">
      <button id="surf-cm-trigger" style="display:flex;align-items:center;gap:7px;width:100%;padding:3px 6px;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:4px;cursor:pointer;font-size:11px;color:var(--text);box-sizing:border-box;">
        <span style="display:inline-block;width:52px;height:10px;border-radius:2px;background:${cmGradCurrent};flex-shrink:0;"></span>
        <span style="flex:1;text-align:left;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${esc(cmSelected.label)}</span>
        <span style="color:var(--muted);font-size:9px;">▾</span>
      </button>
      ${cmPickerOpen ? `<div id="surf-cm-dropdown" style="position:absolute;top:calc(100% + 2px);left:0;right:0;z-index:9999;background:rgba(2,6,23,0.97);border:1px solid rgba(148,163,184,0.2);border-radius:4px;overflow-y:auto;max-height:220px;box-shadow:0 8px 24px rgba(0,0,0,0.7);">
        ${COLORMAPS.map(c => {
          const active = st.colormapName === c.value;
          const grad = colormapGradientCss(c.value);
          return `<button data-surf-cm="${esc(c.value)}" style="display:flex;align-items:center;gap:8px;width:100%;padding:5px 8px;background:${active?"rgba(56,189,248,0.1)":"none"};border:none;border-bottom:1px solid rgba(148,163,184,0.06);cursor:pointer;font-size:11px;color:${active?"#38bdf8":"var(--text)"};text-align:left;box-sizing:border-box;">
            <span style="display:inline-block;width:52px;height:10px;border-radius:2px;background:${grad};flex-shrink:0;"></span>
            <span>${esc(c.label)}</span>
          </button>`;
        }).join("")}
      </div>` : ""}
    </div>`;

    // transparent button style
    const transBtnSty = (active) => `background:none;border:none;cursor:pointer;font-size:15px;padding:0 3px;line-height:1;color:${active?"#38bdf8":"rgba(148,163,184,0.4)"};`;

    const sunR = (st.hillshadeAltitude/90)*36;
    const azRad = st.hillshadeAzimuth*Math.PI/180;
    const sunCx = (40+sunR*Math.sin(azRad)).toFixed(1);
    const sunCy = (40-sunR*Math.cos(azRad)).toFixed(1);
    const unit = st.displayUnit || "ft";

    // Polar dial tick marks (16 ticks every 22.5°; cardinals long, intercardinals medium, fine short)
    const sunDialTicks = Array.from({length:16}, (_, i) => {
      const a = i * Math.PI / 8;
      const isCard = i % 4 === 0, isIntercard = i % 2 === 0;
      const r1 = isCard ? 27 : isIntercard ? 30 : 33;
      const x1 = (40 + r1 * Math.sin(a)).toFixed(1), y1 = (40 - r1 * Math.cos(a)).toFixed(1);
      const x2 = (40 + 36 * Math.sin(a)).toFixed(1), y2 = (40 - 36 * Math.cos(a)).toFixed(1);
      const str = isCard ? "rgba(148,163,184,0.55)" : isIntercard ? "rgba(148,163,184,0.3)" : "rgba(148,163,184,0.18)";
      return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${str}" stroke-width="${isCard?"1":"0.6"}"/>`;
    }).join("");

    // Pre-compute histogram + custom legend HTML outside template literal
    const isSolid = st.colormapName === "solid";
    let histogramSectionHtml;
    let customLegendHtml = "";
    if (isSolid) {
      const lo = histLo, hi = histHi, range2 = hi - lo || 1;
      const divs = histData ? ensureCustomDividers(sel, lo, hi) : [];
      const divBarsHtml = divs.map((v, i) => {
        const pct = Math.max(0, Math.min(100, (v - lo) / range2 * 100));
        return `<div id="surf-hist-divider-${i}" style="${barDivSty(pct)}"></div>` +
               `<div id="surf-hist-divider-label-${i}" style="${lblFollowSty(pct)}">${v.toFixed(1)} ${esc(unit)}</div>`;
      }).join("");
      histogramSectionHtml =
        `<div id="surf-hist-wrap" style="position:relative;padding-bottom:20px;margin-bottom:4px;width:100%;">` +
          histSvgEl +
          `<div style="position:absolute;top:0;left:0;right:0;overflow:visible;pointer-events:none;">${divBarsHtml}</div>` +
        `</div>` +
        `<div style="display:flex;justify-content:space-between;font-size:8px;color:#475569;padding:0 2px;margin-bottom:6px;">` +
          `<span>${lo.toFixed(1)} ${esc(unit)}</span><span>${hi.toFixed(1)} ${esc(unit)}</span>` +
        `</div>`;
      const checkeredBg = "repeating-conic-gradient(#aaa 0% 25%,#fff 0% 50%) 0 0/8px 8px";
      const mono = "font-size:11px;font-family:monospace;color:#94a3b8;";
      const uspan = `<span style="font-size:10px;color:#475569;margin-left:2px;">${esc(unit)}</span>`;
      const ps = "position:absolute;top:0;left:0;width:100%;height:100%;opacity:0;cursor:pointer;border:none;padding:0;";
      const tbSty = (act) => `background:none;border:none;padding:0 0 0 2px;cursor:pointer;font-size:10px;line-height:1;color:${act?"#38bdf8":"#475569"};flex-shrink:0;`;
      const headerRow =
        `<div style="display:grid;grid-template-columns:34px 1fr 1fr;border-bottom:1px solid rgba(148,163,184,0.1);background:rgba(2,6,23,0.35);">` +
        `<div></div>` +
        `<div style="padding:2px 6px;font-size:9px;color:#475569;text-transform:uppercase;letter-spacing:0.07em;">From</div>` +
        `<div style="padding:2px 6px;font-size:9px;color:#475569;text-transform:uppercase;letter-spacing:0.07em;">To</div>` +
        `</div>`;
      const binRows = st.customColors.slice(0, st.customBins).map((color, i) => {
        const hex = esc(rgbToHex(color));
        const isTransp = st.customTransparent[i] || false;
        const binLo = i === 0 ? lo : (divs[i-1] ?? lo);
        const binHi = i === st.customBins - 1 ? hi : (divs[i] ?? hi);
        const isLast = i === st.customBins - 1;
        const rb = i > 0 ? "border-top:1px solid rgba(148,163,184,0.06);" : "";
        return (
          `<div style="display:grid;grid-template-columns:34px 1fr 1fr;align-items:center;${rb}">` +
          `<div style="display:flex;align-items:center;justify-content:center;gap:1px;">` +
            `<div style="position:relative;width:12px;height:12px;border-radius:2px;overflow:hidden;cursor:pointer;" title="Pick color">` +
              `<div id="surf-custom-swatch-${i}" style="width:12px;height:12px;background:${isTransp?checkeredBg:hex};"></div>` +
              `<input type="color" id="surf-custom-picker-${i}" value="${hex}" style="${ps}"/>` +
            `</div>` +
            `<button id="surf-custom-transp-${i}" style="${tbSty(isTransp)}">∅</button>` +
          `</div>` +
          `<div style="padding:3px 6px;"><span id="surf-custom-from-${i}" style="${mono}">${binLo.toFixed(1)}</span>${uspan}</div>` +
          `<div style="padding:3px 6px;">${isLast
            ? `<span style="${mono}">${binHi.toFixed(1)}</span>${uspan}`
            : `<input id="surf-custom-to-${i}" type="number" step="0.1" value="${binHi.toFixed(1)}" style="width:60px;font-size:11px;font-family:monospace;"/>${uspan}`
          }</div>` +
          `</div>`
        );
      }).join("");
      customLegendHtml = `<div style="border:1px solid rgba(148,163,184,0.12);border-radius:5px;overflow:hidden;margin-bottom:8px;">${headerRow}${binRows}</div>`;
    } else {
      const uCol = st.underflowTransparent ? "repeating-conic-gradient(#555 0% 25%,#888 0% 50%) 0 0/6px 6px" : rgbToHex(st.underflowColor);
      const oCol = st.overflowTransparent ? "repeating-conic-gradient(#555 0% 25%,#888 0% 50%) 0 0/6px 6px" : rgbToHex(st.overflowColor);
      const swatchSty = "position:relative;width:11px;height:11px;border-radius:2px;overflow:hidden;cursor:pointer;flex-shrink:0;border:1px solid rgba(148,163,184,0.25);";
      const transBtnSmSty = (act) =>
        `display:inline-flex;align-items:center;justify-content:center;width:12px;height:12px;border-radius:2px;` +
        `cursor:pointer;border:1px solid rgba(148,163,184,0.3);padding:0;flex-shrink:0;font-size:8px;line-height:1;` +
        `background:${act?"repeating-conic-gradient(#555 0% 25%,#888 0% 50%) 0 0/6px 6px":"rgba(15,23,42,0.5)"};` +
        `color:${act?"#38bdf8":"rgba(148,163,184,0.4)"};`;
      histogramSectionHtml =
        `<div id="surf-hist-wrap" style="position:relative;padding-bottom:24px;margin-bottom:8px;width:100%;">` +
          histSvgEl +
          `<div id="surf-hist-bar-min" style="position:absolute;top:0;height:52px;width:14px;background:transparent;cursor:ew-resize;left:${pMin.toFixed(2)}%;transform:translateX(-50%);pointer-events:all;">` +
            `<div style="position:absolute;left:6px;top:0;bottom:0;width:2px;background:rgba(255,255,255,0.9);box-shadow:0 0 4px rgba(0,0,0,0.6);"></div>` +
            `<div style="position:absolute;left:3px;bottom:0;width:8px;height:8px;border-radius:50%;background:#e2e8f0;box-shadow:0 0 4px rgba(0,0,0,0.6);"></div>` +
            `<div id="surf-hist-label-min" style="position:absolute;top:54px;left:50%;transform:translateX(-50%);font-size:8px;color:#e2e8f0;white-space:nowrap;background:rgba(2,6,23,0.85);padding:1px 4px;border-radius:3px;border:1px solid rgba(255,255,255,0.15);pointer-events:none;z-index:3;">${rMin.toFixed(1)} ${esc(unit)}</div>` +
          `</div>` +
          `<div id="surf-hist-bar-max" style="position:absolute;top:0;height:52px;width:14px;background:transparent;cursor:ew-resize;left:${pMax.toFixed(2)}%;transform:translateX(-50%);pointer-events:all;">` +
            `<div style="position:absolute;left:6px;top:0;bottom:0;width:2px;background:rgba(255,255,255,0.9);box-shadow:0 0 4px rgba(0,0,0,0.6);"></div>` +
            `<div style="position:absolute;left:3px;bottom:0;width:8px;height:8px;border-radius:50%;background:#e2e8f0;box-shadow:0 0 4px rgba(0,0,0,0.6);"></div>` +
            `<div id="surf-hist-label-max" style="position:absolute;top:54px;left:50%;transform:translateX(-50%);font-size:8px;color:#e2e8f0;white-space:nowrap;background:rgba(2,6,23,0.85);padding:1px 4px;border-radius:3px;border:1px solid rgba(255,255,255,0.15);pointer-events:none;z-index:3;">${rMax.toFixed(1)} ${esc(unit)}</div>` +
          `</div>` +
          `<div style="position:absolute;top:2px;left:2px;display:flex;align-items:center;gap:1px;pointer-events:all;z-index:2;">` +
            `<div style="${swatchSty}" title="Underflow color">` +
              `<div style="position:absolute;inset:0;background:${uCol};"></div>` +
              `<input id="surf-underflow-picker" type="color" value="${rgbToHex(st.underflowColor)}" style="opacity:0;position:absolute;inset:0;width:100%;height:100%;cursor:pointer;border:none;padding:0;" />` +
            `</div>` +
            `<button id="surf-underflow-transp" style="${transBtnSmSty(st.underflowTransparent)}" title="Toggle transparent">⌀</button>` +
          `</div>` +
          `<div style="position:absolute;top:2px;right:2px;display:flex;align-items:center;gap:1px;pointer-events:all;z-index:2;">` +
            `<button id="surf-overflow-transp" style="${transBtnSmSty(st.overflowTransparent)}" title="Toggle transparent">⌀</button>` +
            `<div style="${swatchSty}" title="Overflow color">` +
              `<div style="position:absolute;inset:0;background:${oCol};"></div>` +
              `<input id="surf-overflow-picker" type="color" value="${rgbToHex(st.overflowColor)}" style="opacity:0;position:absolute;inset:0;width:100%;height:100%;cursor:pointer;border:none;padding:0;" />` +
            `</div>` +
          `</div>` +
        `</div>`;
    }

    // Pre-compute adjuster slider stack HTML (with/without Z-factor)
    const zFactor    = st.tileZFactor    ?? 1.0;
    const saturation = st.tileSaturation ?? 1.0;
    const contrast   = st.tileContrast   ?? 1.0;
    const slrRow = (lbl, id, val, disp, mn, mx) =>
      `<div style="display:flex;align-items:center;gap:5px;margin-bottom:6px;">` +
      `<span style="font-size:9px;color:var(--muted);white-space:nowrap;min-width:54px;">${lbl}</span>` +
      `<input id="${id}" type="range" min="${mn}" max="${mx}" value="${val}" style="flex:1;min-width:0;margin:0;" />` +
      `<span id="${id}-val" style="font-size:9px;color:var(--text);font-family:monospace;min-width:26px;text-align:right;white-space:nowrap;">${disp}</span>` +
      `</div>`;
    const adjDefaulted = zFactor === 1.0 && saturation === 1.0 && contrast === 1.0 && st.tileGamma === 1.0;
    const adjSliders = (withZ) =>
      `<div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;">` +
      `<div style="display:flex;justify-content:flex-end;margin-bottom:1px;">` +
      `<button id="surf-adj-reset" style="background:none;border:none;cursor:pointer;font-size:9px;padding:0;line-height:1;color:${adjDefaulted?"rgba(148,163,184,0.2)":"rgba(148,163,184,0.55)"};" title="Reset to defaults">↺ reset</button>` +
      `</div>` +
      (withZ ? slrRow("Z-factor","surf-zfactor",Math.round(zFactor*10),zFactor.toFixed(1)+"×",10,100) : "") +
      slrRow("Saturation","surf-saturation",Math.round(saturation*100),saturation.toFixed(2),0,300) +
      slrRow("Contrast","surf-contrast",Math.round(contrast*100),contrast.toFixed(2),0,300) +
      slrRow("Gamma","surf-gamma",Math.round(st.tileGamma*100),st.tileGamma.toFixed(2),10,300) +
      `</div>`;

    vizHtml = `
    <div style="border-top:2px solid rgba(56,189,248,0.25);margin-top:2px;padding:10px;width:100%;box-sizing:border-box;">
      <div class="small" style="font-weight:600;color:#38bdf8;margin-bottom:${selectedIds.size > 1 ? "3" : "6"}px;">${esc(sel.name)}</div>
      ${selectedIds.size > 1 ? `<div class="small" style="color:#94a3b8;font-size:9px;margin-bottom:6px;">${selectedIds.size} surfaces selected — settings apply to all</div>` : ""}
      ${selectedIds.size === 1 ? (() => {
        const gridRes = sel.resolution ?? 1;
        const gridUnit = sel.settings.displayUnit || "ft";
        const resOpts = [0.25, 0.5, 1, 2, 5].map(v =>
          `<option value="${v}"${v === gridRes ? " selected" : ""}>${v} ${gridUnit}</option>`).join("");
        if (rebuildOpen) {
          return `<div style="background:rgba(0,0,0,0.25);border-radius:6px;padding:7px 8px;margin-bottom:8px;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;flex-wrap:wrap;">
              <span class="small" style="color:var(--muted);white-space:nowrap;font-size:9px;">EPSG:</span>
              <input id="surf-rebuild-epsg" type="text" value="${esc(sel.sourceEpsg||"")}" placeholder="auto" style="width:56px;font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 4px;" />
              <span class="small" style="color:var(--muted);white-space:nowrap;font-size:9px;">Res:</span>
              <select id="surf-rebuild-res" style="font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 3px;">${resOpts}</select>
            </div>
            <div style="display:flex;gap:5px;">
              <button id="surf-rebuild-go" style="font-size:10px;padding:2px 8px;background:rgba(56,189,248,0.15);border:1px solid rgba(56,189,248,0.35);border-radius:4px;color:#38bdf8;cursor:pointer;">↺ Rebuild</button>
              <button id="surf-rebuild-cancel" style="font-size:10px;padding:2px 8px;background:none;border:1px solid rgba(148,163,184,0.2);border-radius:4px;color:var(--muted);cursor:pointer;">Cancel</button>
            </div>
          </div>`;
        } else {
          return `<div style="display:flex;align-items:center;gap:5px;margin-bottom:8px;">
            <span class="small" style="color:var(--muted);font-size:9px;">Grid:</span>
            <span class="small" style="font-size:9px;font-family:monospace;">${gridRes} ${gridUnit}</span>
            <button id="surf-rebuild-toggle" style="font-size:9px;padding:1px 5px;background:none;border:1px solid rgba(148,163,184,0.2);border-radius:3px;color:var(--muted);cursor:pointer;margin-left:2px;">⚙ Rebuild</button>
          </div>`;
        }
      })() : ""}

      <!-- Colormap row -->
      <div style="display:flex;gap:10px;align-items:flex-end;margin-bottom:8px;">
        <div style="flex:1;min-width:0;">
          <div class="small" style="color:var(--muted);margin-bottom:4px;">Colormap</div>
          ${cmDropdownHtml}
        </div>
        ${isSolid ? `<div style="flex-shrink:0;"><div class="small" style="color:var(--muted);margin-bottom:4px;">Bins</div><input id="surf-custom-bins" type="number" min="2" max="8" value="${st.customBins}" style="width:48px;font-size:11px;" /></div>` : ""}
      </div>

      <!-- Histogram -->
      ${isMulti ? `<div style="font-size:9px;color:#94a3b8;margin-bottom:3px;">Combined histogram · ${histReadyCount} of ${selectedIds.size} surfaces loaded</div>` : ""}
      ${histogramSectionHtml}
      ${customLegendHtml}

      <!-- Hillshade + adjuster stack -->
      <div style="margin-bottom:4px;">
        <label class="small"><input id="surf-hillshade" type="checkbox" ${st.hillshadeOverlay?"checked":""} style="margin-right:5px;" />Hillshade</label>
      </div>
      ${st.hillshadeOverlay ? `
      <div style="display:flex;gap:8px;align-items:stretch;margin-bottom:6px;">
        <div style="padding:6px;background:rgba(0,0,0,0.2);border-radius:6px;flex-shrink:0;display:flex;flex-direction:column;align-items:center;justify-content:center;">
          <svg id="surf-sun-svg" viewBox="0 0 80 80" style="width:70px;height:70px;cursor:crosshair;display:block;">
            <circle cx="40" cy="40" r="38" fill="rgba(15,23,42,0.8)" stroke="rgba(148,163,184,0.25)" stroke-width="1"/>
            <circle cx="40" cy="40" r="12" fill="none" stroke="rgba(148,163,184,0.12)" stroke-width="0.5" stroke-dasharray="2,3"/>
            <circle cx="40" cy="40" r="24" fill="none" stroke="rgba(148,163,184,0.15)" stroke-width="0.5" stroke-dasharray="2,3"/>
            <circle cx="40" cy="40" r="36" fill="none" stroke="rgba(148,163,184,0.25)" stroke-width="0.5"/>
            ${sunDialTicks}
            <text x="40" y="10" text-anchor="middle" font-size="7" fill="rgba(148,163,184,0.8)" font-weight="600">N</text>
            <text x="40" y="76" text-anchor="middle" font-size="7" fill="rgba(148,163,184,0.45)">S</text>
            <text x="8"  y="43" text-anchor="middle" font-size="7" fill="rgba(148,163,184,0.45)">W</text>
            <text x="72" y="43" text-anchor="middle" font-size="7" fill="rgba(148,163,184,0.45)">E</text>
            <text x="54" y="43" font-size="4.5" fill="rgba(148,163,184,0.28)">30°</text>
            <text x="66" y="43" font-size="4.5" fill="rgba(148,163,184,0.22)">60°</text>
            <circle id="surf-sun-glow" cx="${sunCx}" cy="${sunCy}" r="9" fill="rgba(251,191,36,0.2)"/>
            <circle id="surf-sun-dot"  cx="${sunCx}" cy="${sunCy}" r="5" fill="#fbbf24" stroke="rgba(255,255,255,0.9)" stroke-width="1.5"/>
          </svg>
          <div id="surf-sun-label" class="small" style="text-align:center;color:var(--muted);font-size:9px;margin-top:3px;">Az ${st.hillshadeAzimuth}° · Alt ${st.hillshadeAltitude}°</div>
        </div>
        ${adjSliders(true)}
      </div>
      ` : `<div style="margin-bottom:6px;">${adjSliders(false)}</div>`}
    </div>`;
  } else if (sel && !sel.rasterReady) {
    const isError = sel.buildJob?.status === "error";
    vizHtml = `
    <div style="border-top:2px solid rgba(148,163,184,0.15);margin-top:2px;padding:10px;width:100%;box-sizing:border-box;">
      <div class="small" style="font-weight:600;color:var(--muted);margin-bottom:6px;">${esc(sel.name)}</div>
      ${isError
        ? `<div class="small" style="color:#f87171;margin-bottom:6px;">Build failed: ${esc(sel.buildJob?.message||"unknown error")}</div>
           <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
             <span class="small" style="color:var(--muted);white-space:nowrap;">Source EPSG:</span>
             <input id="surf-retry-epsg" type="text" value="${esc(sel.sourceEpsg||"")}" placeholder="e.g. 2227" style="width:80px;font-size:11px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:2px 5px;" title="EPSG code for source CRS (e.g. 2227 = CA State Plane Zone III US Feet)" />
           </div>
           <button id="surf-retry-build" style="${btnSty}">Retry Build</button>`
        : `<div class="small" style="color:#fbbf24;">Building raster… ${sel.buildJob?.progressPct?sel.buildJob.progressPct+"%":""}</div>
           <div class="small" style="color:var(--muted);margin-top:2px;">${esc(sel.buildJob?.message||"Processing point cloud…")}</div>
           <div style="margin-top:8px;height:4px;border-radius:2px;background:rgba(148,163,184,0.15);overflow:hidden;"><div style="height:100%;border-radius:2px;background:linear-gradient(90deg,#fbbf24,#f59e0b);width:${sel.buildJob?.progressPct??5}%;transition:width 0.5s;"></div></div>
           <div class="small" style="color:var(--muted);margin-top:6px;font-size:10px;">Visualization controls will appear when the raster is ready.</div>`
      }
    </div>`;
  }

  container.innerHTML = `
    <div style="border:1px solid rgba(148,163,184,0.18);border-radius:8px;overflow:hidden;">
      <div style="display:flex;align-items:center;padding:7px 10px;background:rgba(2,6,23,0.5);border-bottom:1px solid rgba(148,163,184,0.12);">
        <span class="small" style="font-weight:600;flex:1;">SURFACES${catalog.length?` (${catalog.length})`:""}${savePending?" — saving…":""}</span>
        <button id="surf-add-btn" style="${btnSty}margin-right:6px;">${addPickerOpen?"▾ Cancel":"+ Add"}</button>
        <button id="surf-refresh-btn" style="${btnSty}" title="Refresh">↻</button>
      </div>
      ${addPickerHtml}
      <div id="surf-catalog-list">${catalogRows}</div>
    </div>
    ${vizHtml}
  `;

  // Fix histogram width after layout settles — % width on position:relative can mis-compute before first paint.
  const histWrap = container.querySelector("#surf-hist-wrap");
  if (histWrap) {
    requestAnimationFrame(() => {
      const par = histWrap.parentElement;
      if (!par) return;
      const cs = getComputedStyle(par);
      const w = par.clientWidth - parseFloat(cs.paddingLeft || "0") - parseFloat(cs.paddingRight || "0");
      if (w > 0) histWrap.style.width = w + "px";
    });
  }

  bindEvents();
}

// ── Event binding ─────────────────────────────────────────────────────────────

function bindEvents() {
  if (!container) return;

  container.querySelector("#surf-add-btn")?.addEventListener("click", () => {
    addPickerOpen = !addPickerOpen;
    if (addPickerOpen) loadAvailable();
    render();
  });
  container.querySelector("#surf-refresh-btn")?.addEventListener("click", () => loadCatalog());

  // Add picker
  container.querySelectorAll("[data-surf-add-pick]").forEach(btn => {
    btn.addEventListener("click", async () => {
      const marina = btn.dataset.surfAddPick;
      const name = btn.dataset.surfAddName || marina;
      const s3Key = btn.dataset.surfAddS3key || "";
      const nativeUnit = btn.dataset.surfAddUnit || "ft";
      const pickRow = btn.closest("div")?.parentElement;
      const epsgInput = pickRow?.querySelector(`[data-surf-epsg-key="${s3Key}"]`);
      const resInput = pickRow?.querySelector(`[data-surf-res-key="${s3Key}"]`);
      const sourceEpsg = epsgInput?.value?.trim() || null;
      const resolution = Number(resInput?.value) || 1;
      const settings = DEFAULT_SETTINGS();
      settings.displayUnit = nativeUnit === "m" ? "m" : "ft";
      const entry = { id: uid(), name, marinaCode: marina, s3Key, sourceEpsg: sourceEpsg || null, resolution, rasterPath: null, rasterReady: false, createdAt: new Date().toISOString(), settings, buildJob: { status: "running", progressPct: 0, message: "Queued…" } };
      catalog.push(entry);
      addPickerOpen = false;
      selectedId = entry.id;
      selectedIds.clear(); selectedIds.add(entry.id);
      try {
        await fetch("/api/surfaces/build", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ surfaceId: entry.id, s3Key, nativeUnit, sourceEpsg, resolution }) });
      } catch {}
      app?.log?.("surface.build.start", { surfaceId: entry.id, name, s3Key, resolution, sourceEpsg: sourceEpsg || null });
      startBuildPoll(entry.id);
      saveCatalog(); render();
    });
  });

  // Rebuild form
  container.querySelector("#surf-rebuild-toggle")?.addEventListener("click", () => { rebuildOpen = true; render(); });
  container.querySelector("#surf-rebuild-cancel")?.addEventListener("click", () => { rebuildOpen = false; render(); });
  container.querySelector("#surf-rebuild-go")?.addEventListener("click", async () => {
    const sel = getSelected();
    if (!sel) return;
    const epsg = container.querySelector("#surf-rebuild-epsg")?.value?.trim() || null;
    const res = Number(container.querySelector("#surf-rebuild-res")?.value) || 1;
    sel.sourceEpsg = epsg || null;
    sel.resolution = res;
    sel.rasterReady = false;
    sel.buildJob = { status: "running", progressPct: 0, message: "Queued…" };
    rebuildOpen = false;
    hideSurface(sel.id);
    stopBuildPoll(sel.id);
    try {
      await fetch("/api/surfaces/build", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ surfaceId: sel.id, s3Key: sel.s3Key, nativeUnit: sel.settings.displayUnit || "ft", sourceEpsg: epsg, resolution: res }) });
    } catch {}
    app?.log?.("surface.rebuild.start", { surfaceId: sel.id, name: sel.name, resolution: res, sourceEpsg: epsg || null });
    startBuildPoll(sel.id);
    saveCatalog(); render();
  });

  // Visibility
  container.querySelectorAll("[data-surf-vis]").forEach(cb => {
    cb.addEventListener("change", e => { e.target.checked ? showSurface(cb.dataset.surfVis) : hideSurface(cb.dataset.surfVis); });
  });

  // Per-row opacity slider
  container.querySelectorAll("[data-surf-opacity]").forEach(slider => {
    const id = slider.dataset.surfOpacity;
    slider.addEventListener("mousedown", () => {
      const row = slider.closest("[data-surf-row]");
      if (!row) return;
      row.draggable = false;
      const restore = () => { row.draggable = true; document.removeEventListener("mouseup", restore); };
      document.addEventListener("mouseup", restore);
    });
    slider.addEventListener("input", e => {
      const s = catalog.find(c => c.id === id);
      if (!s) return;
      s.settings.tileOpacity = Number(e.target.value) / 100;
      const lbl = container.querySelector(`[data-surf-opacity-label="${id}"]`);
      if (lbl) lbl.textContent = `${Math.round(s.settings.tileOpacity*100)}%`;
      const rt = surfaceRuntimes.get(id);
      if (rt?.tileLayer) rt.tileLayer.setOpacity(s.settings.tileOpacity);
      if (rt?.hillshadeLayer) rt.hillshadeLayer.setOpacity(s.settings.tileOpacity);
    });
    slider.addEventListener("change", () => saveCatalog());
  });

  // Select for viz controls (shift = range, ctrl/meta = toggle, plain = exclusive)
  container.querySelectorAll("[data-surf-select]").forEach(el => {
    el.addEventListener("click", e => {
      const id = el.dataset.surfSelect;
      rebuildOpen = false;
      if (e.ctrlKey || e.metaKey) {
        if (selectedIds.has(id)) {
          selectedIds.delete(id);
          if (selectedId === id) selectedId = [...selectedIds][selectedIds.size - 1] ?? null;
        } else {
          selectedIds.add(id);
          selectedId = id;
        }
      } else if (e.shiftKey && selectedId) {
        const ids = catalog.map(s => s.id);
        const lo = Math.min(ids.indexOf(selectedId), ids.indexOf(id));
        const hi = Math.max(ids.indexOf(selectedId), ids.indexOf(id));
        for (let i = lo; i <= hi; i++) selectedIds.add(ids[i]);
        selectedId = id;
      } else {
        if (selectedIds.size === 1 && selectedIds.has(id)) {
          selectedIds.clear(); selectedId = null;
        } else {
          selectedIds.clear(); selectedIds.add(id); selectedId = id;
        }
      }
      render();
    });
  });

  // Rename
  container.querySelectorAll("[data-surf-rename]").forEach(btn => {
    btn.addEventListener("click", () => {
      const s = catalog.find(c => c.id === btn.dataset.surfRename);
      if (!s) return;
      const n = prompt("Rename surface:", s.name);
      if (n?.trim()) { s.name = n.trim(); saveCatalog(); render(); }
    });
  });

  // Remove
  container.querySelectorAll("[data-surf-remove]").forEach(btn => {
    btn.addEventListener("click", () => {
      if (!confirm("Remove this surface from the catalog?")) return;
      const id = btn.dataset.surfRemove;
      stopBuildPoll(id); hideSurface(id); surfaceRuntimes.delete(id);
      catalog = catalog.filter(s => s.id !== id);
      selectedIds.delete(id);
      if (selectedId === id) selectedId = [...selectedIds][selectedIds.size - 1] ?? null;
      saveCatalog(); render();
    });
  });

  // Retry build
  container.querySelector("#surf-retry-build")?.addEventListener("click", async () => {
    const s = getSelected();
    if (!s) return;
    const epsgInput = container.querySelector("#surf-retry-epsg");
    const sourceEpsg = epsgInput?.value?.trim() || null;
    if (sourceEpsg) s.sourceEpsg = sourceEpsg;
    s.buildJob = { status: "running", progressPct: 0, message: "Queued…" };
    try {
      await fetch("/api/surfaces/build", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ surfaceId: s.id, s3Key: s.s3Key, nativeUnit: s.settings.displayUnit, sourceEpsg: s.sourceEpsg || null }) });
    } catch {}
    startBuildPoll(s.id); saveCatalog(); render();
  });

  // Drag-and-drop reorder
  container.querySelectorAll("[data-surf-row]").forEach(row => {
    row.addEventListener("dragstart", e => { dragSrcId = row.dataset.surfRow; e.dataTransfer.effectAllowed = "move"; });
    row.addEventListener("dragover", e => { e.preventDefault(); row.style.outline = "1px solid rgba(56,189,248,0.4)"; });
    row.addEventListener("dragleave", () => { row.style.outline = ""; });
    row.addEventListener("drop", e => {
      e.preventDefault(); row.style.outline = "";
      const dropId = row.dataset.surfRow;
      if (!dragSrcId || dragSrcId === dropId) return;
      const fi = catalog.findIndex(s => s.id === dragSrcId);
      const ti = catalog.findIndex(s => s.id === dropId);
      if (fi < 0 || ti < 0) return;
      const [moved] = catalog.splice(fi, 1);
      catalog.splice(ti, 0, moved);
      reapplyZIndices(); saveCatalog(); render();
    });
    row.addEventListener("dragend", () => { dragSrcId = null; });
  });

  // ── Viz controls (raster-ready surfaces only) ────────────────────────────
  const sel = getSelected();
  if (!sel?.rasterReady) return;
  const st = sel.settings;
  const rt = surfaceRuntimes.get(sel.id);
  function settingChanged() { saveCatalog(); for (const id of selectedIds) { if (visibleIds.has(id)) refreshSurface(id); } }

  // Colormap dropdown
  if (closeCmDropdown) { document.removeEventListener("mousedown", closeCmDropdown); closeCmDropdown = null; }
  container.querySelector("#surf-cm-trigger")?.addEventListener("click", e => {
    e.stopPropagation();
    cmPickerOpen = !cmPickerOpen;
    render();
    if (cmPickerOpen) {
      closeCmDropdown = ev => {
        const dropdown = container.querySelector("#surf-cm-dropdown");
        const trigger = container.querySelector("#surf-cm-trigger");
        if (dropdown?.contains(ev.target) || trigger?.contains(ev.target)) return;
        cmPickerOpen = false;
        document.removeEventListener("mousedown", closeCmDropdown);
        closeCmDropdown = null;
        render();
      };
      setTimeout(() => document.addEventListener("mousedown", closeCmDropdown), 0);
    }
  });
  container.querySelectorAll("[data-surf-cm]").forEach(btn => {
    btn.addEventListener("click", () => {
      const newCm = btn.dataset.surfCm;
      applyToAllSelected(s => { s.settings.colormapName = newCm; });
      cmPickerOpen = false;
      if (closeCmDropdown) { document.removeEventListener("mousedown", closeCmDropdown); closeCmDropdown = null; }
      settingChanged(); render();
    });
  });


  // Histogram drag handles (standard colormap mode — min/max rescale bars)
  function startHistDrag(which) {
    return function(e) {
      e.preventDefault();
      const histWrap = container.querySelector("#surf-hist-wrap");
      if (!histWrap) return;
      const parentRect = histWrap.getBoundingClientRect();
      const activeHistData = lastMergedHistData ?? rt?.histData;
      const histLo = activeHistData?.[1]?.[0] ?? (st.rescaleMin ?? -50);
      const histHi = activeHistData?.[1]?.[activeHistData[1].length-1] ?? (st.rescaleMax ?? 0);
      const range = histHi - histLo || 1;
      function onMove(ev) {
        const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
        const pct = Math.max(0, Math.min(100, (clientX - parentRect.left) / parentRect.width * 100));
        const val = Math.round((histLo + pct/100*range)*10)/10;
        if (which === "min") {
          st.rescaleMin = Math.min(val, (st.rescaleMax ?? histHi) - 0.1);
          const p = Math.max(0, Math.min(100, (st.rescaleMin-histLo)/range*100));
          const bar = container.querySelector("#surf-hist-bar-min");
          const lbl = container.querySelector("#surf-hist-label-min");
          if (bar) bar.style.left = `${p.toFixed(2)}%`;
          if (lbl) lbl.textContent = `${st.rescaleMin.toFixed(1)} ${st.displayUnit}`;
        } else {
          st.rescaleMax = Math.max(val, (st.rescaleMin ?? histLo) + 0.1);
          const p = Math.max(0, Math.min(100, (st.rescaleMax-histLo)/range*100));
          const bar = container.querySelector("#surf-hist-bar-max");
          const lbl = container.querySelector("#surf-hist-label-max");
          if (bar) bar.style.left = `${p.toFixed(2)}%`;
          if (lbl) lbl.textContent = `${st.rescaleMax.toFixed(1)} ${st.displayUnit}`;
        }
        const svgEl = container.querySelector("#surf-hist-svg");
        if (svgEl && activeHistData) svgEl.innerHTML = generateHistogramBars(activeHistData[0], activeHistData[1], sel);
      }
      function onUp() {
        document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp);
        document.removeEventListener("touchmove", onMove); document.removeEventListener("touchend", onUp);
        const min = st.rescaleMin, max = st.rescaleMax;
        applyToAllSelected(s => { s.settings.rescaleMin = min; s.settings.rescaleMax = max; });
        settingChanged();
      }
      document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp);
      document.addEventListener("touchmove", onMove, { passive:false }); document.addEventListener("touchend", onUp);
    };
  }
  container.querySelector("#surf-hist-bar-min")?.addEventListener("mousedown", startHistDrag("min"));
  container.querySelector("#surf-hist-bar-max")?.addEventListener("mousedown", startHistDrag("max"));
  container.querySelector("#surf-hist-bar-min")?.addEventListener("touchstart", startHistDrag("min"), { passive:false });
  container.querySelector("#surf-hist-bar-max")?.addEventListener("touchstart", startHistDrag("max"), { passive:false });

  // Custom colormap: draggable divider bars
  const solidHistData = lastMergedHistData ?? rt?.histData;
  if (st.colormapName === "solid" && solidHistData) {
    const histWrap = container.querySelector("#surf-hist-wrap");
    const lo = solidHistData[1][0], hi = solidHistData[1][solidHistData[1].length-1], range2 = hi - lo || 1;
    const divs = ensureCustomDividers(sel, lo, hi);
    divs.forEach((_, idx) => {
      const bar = container.querySelector(`#surf-hist-divider-${idx}`);
      if (!bar) return;
      function makeDivDrag(startEvt) {
        startEvt.preventDefault();
        const rect = histWrap.getBoundingClientRect();
        function onMove(ev) {
          const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
          const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
          const val = Math.round((lo + pct * range2) * 10) / 10;
          const sorted = [...st.customDividers].sort((a, b) => a - b);
          const prev = idx === 0 ? lo + 0.1 : sorted[idx-1] + 0.1;
          const next = idx === sorted.length-1 ? hi - 0.1 : sorted[idx+1] - 0.1;
          sorted[idx] = Math.max(prev, Math.min(next, val));
          st.customDividers = sorted;
          // update bar + label positions live
          sorted.forEach((v, i) => {
            const p = Math.max(0, Math.min(100, (v - lo) / range2 * 100));
            const b = container.querySelector(`#surf-hist-divider-${i}`);
            const l = container.querySelector(`#surf-hist-divider-label-${i}`);
            if (b) b.style.left = `${p.toFixed(2)}%`;
            if (l) { l.style.left = barLabelLeft(p); l.style.transform = barLabelTransform(p); l.textContent = `${v.toFixed(1)} ${st.displayUnit}`; }
          });
          // update table from-cells and to-inputs
          const recomputedDivs = [...st.customDividers].sort((a,b) => a-b);
          for (let i = 0; i < st.customBins; i++) {
            const fromEl = container.querySelector(`#surf-custom-from-${i}`);
            if (fromEl) fromEl.textContent = (i === 0 ? lo : recomputedDivs[i-1]).toFixed(1);
            if (i < st.customBins - 1) {
              const toInput = container.querySelector(`#surf-custom-to-${i}`);
              if (toInput && document.activeElement !== toInput) toInput.value = recomputedDivs[i].toFixed(1);
            }
          }
          const svgEl = container.querySelector("#surf-hist-svg");
          if (svgEl) svgEl.innerHTML = generateHistogramBars(solidHistData[0], solidHistData[1], sel);
          refreshSurface(sel.id);
        }
        function onUp() {
          document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp);
          document.removeEventListener("touchmove", onMove); document.removeEventListener("touchend", onUp);
          const finalDivs = [...st.customDividers];
          applyToAllSelected(s => { if (s.id !== sel.id) s.settings.customDividers = [...finalDivs]; });
          settingChanged();
        }
        document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp);
        document.addEventListener("touchmove", onMove, { passive:false }); document.addEventListener("touchend", onUp);
      }
      bar.style.pointerEvents = "all";
      bar.addEventListener("mousedown", makeDivDrag);
      bar.addEventListener("touchstart", makeDivDrag, { passive:false });
    });
  }

  // Custom colormap: bins spinner
  container.querySelector("#surf-custom-bins")?.addEventListener("change", e => {
    const newBins = Math.max(2, Math.min(8, parseInt(e.target.value, 10) || st.customBins));
    applyToAllSelected(s => {
      const sRt = surfaceRuntimes.get(s.id);
      const sLo = sRt?.histData?.[1]?.[0] ?? -50;
      const sHi = sRt?.histData?.[1]?.[sRt.histData[1].length - 1] ?? 0;
      ensureCustomDividers(s, sLo, sHi);
      const oldDivs = [...s.settings.customDividers].sort((a, b) => a - b);
      const newDivCount = newBins - 1;
      if (newDivCount > oldDivs.length) {
        const lastDiv = oldDivs[oldDivs.length - 1] ?? sLo;
        s.settings.customDividers = [...oldDivs, (lastDiv + sHi) / 2];
      } else {
        s.settings.customDividers = oldDivs.slice(0, newDivCount);
      }
      s.settings.customBins = newBins;
    });
    settingChanged(); render();
  });

  // Custom colormap: color pickers + transparency + to-inputs
  for (let i = 0; i < st.customBins; i++) {
    const idx = i;
    container.querySelector(`#surf-custom-picker-${idx}`)?.addEventListener("input", e => {
      const rgb = hexToRgb(e.target.value);
      applyToAllSelected(s => { s.settings.customColors[idx] = [...rgb]; });
      const sw = container.querySelector(`#surf-custom-swatch-${idx}`);
      if (sw) sw.style.background = e.target.value;
      const svgEl = container.querySelector("#surf-hist-svg");
      const ahd = lastMergedHistData ?? rt?.histData;
      if (svgEl && ahd) svgEl.innerHTML = generateHistogramBars(ahd[0], ahd[1], sel);
      for (const id of selectedIds) if (visibleIds.has(id)) refreshSurface(id);
    });
    container.querySelector(`#surf-custom-picker-${idx}`)?.addEventListener("change", () => saveCatalog());
    container.querySelector(`#surf-custom-transp-${idx}`)?.addEventListener("click", () => {
      const newTransp = !st.customTransparent[idx];
      applyToAllSelected(s => { s.settings.customTransparent[idx] = newTransp; });
      settingChanged(); render();
    });
    container.querySelector(`#surf-custom-to-${idx}`)?.addEventListener("change", e => {
      const newVal = parseFloat(e.target.value);
      if (isNaN(newVal)) return;
      applyToAllSelected(s => {
        const sRt = surfaceRuntimes.get(s.id);
        const sLo = sRt?.histData?.[1]?.[0] ?? -50;
        const sHi = sRt?.histData?.[1]?.[sRt.histData[1].length - 1] ?? 0;
        ensureCustomDividers(s, sLo, sHi);
        const sorted = [...s.settings.customDividers].sort((a, b) => a - b);
        sorted[idx] = newVal;
        s.settings.customDividers = sorted;
      });
      settingChanged(); render();
    });
  }

  // Underflow/overflow
  container.querySelector("#surf-underflow-picker")?.addEventListener("input", e => {
    const rgb = hexToRgb(e.target.value);
    applyToAllSelected(s => { s.settings.underflowColor = [...rgb]; });
    // Update swatch directly — avoid render() which would close the native picker
    const swInner = e.target.previousElementSibling;
    if (swInner && !st.underflowTransparent) swInner.style.background = e.target.value;
    const svgEl = container.querySelector("#surf-hist-svg");
    const ahd = lastMergedHistData ?? rt?.histData;
    if (svgEl && ahd) svgEl.innerHTML = generateHistogramBars(ahd[0], ahd[1], sel);
    for (const id of selectedIds) if (visibleIds.has(id)) refreshSurface(id);
  });
  container.querySelector("#surf-underflow-picker")?.addEventListener("change", () => saveCatalog());
  container.querySelector("#surf-underflow-transp")?.addEventListener("click", () => {
    const newVal = !st.underflowTransparent;
    applyToAllSelected(s => { s.settings.underflowTransparent = newVal; });
    settingChanged(); render();
  });
  container.querySelector("#surf-overflow-picker")?.addEventListener("input", e => {
    const rgb = hexToRgb(e.target.value);
    applyToAllSelected(s => { s.settings.overflowColor = [...rgb]; });
    // Update swatch directly — avoid render() which would close the native picker
    const swInner = e.target.previousElementSibling;
    if (swInner && !st.overflowTransparent) swInner.style.background = e.target.value;
    const svgEl = container.querySelector("#surf-hist-svg");
    const ahd = lastMergedHistData ?? rt?.histData;
    if (svgEl && ahd) svgEl.innerHTML = generateHistogramBars(ahd[0], ahd[1], sel);
    for (const id of selectedIds) if (visibleIds.has(id)) refreshSurface(id);
  });
  container.querySelector("#surf-overflow-picker")?.addEventListener("change", () => saveCatalog());
  container.querySelector("#surf-overflow-transp")?.addEventListener("click", () => {
    const newVal = !st.overflowTransparent;
    applyToAllSelected(s => { s.settings.overflowTransparent = newVal; });
    settingChanged(); render();
  });

  // Hillshade toggle
  container.querySelector("#surf-hillshade")?.addEventListener("change", e => {
    const checked = e.target.checked;
    applyToAllSelected(s => {
      s.settings.hillshadeOverlay = checked;
      const sRt = surfaceRuntimes.get(s.id);
      if (!sRt) return;
      if (checked && visibleIds.has(s.id) && !sRt.hillshadeLayer) {
        sRt.hillshadeLayer = createHillshadeLayer(s, sRt, app.map.instance, sRt.colormapId ?? null);
        bindTileProgress(sRt, sRt.hillshadeLayer);
      } else if (!checked && sRt.hillshadeLayer) {
        try { sRt.hillshadeLayer.remove(); } catch {} sRt.hillshadeLayer = null;
      }
    });
    settingChanged(); render();
  });

  // Sun picker
  const sunSvg = container.querySelector("#surf-sun-svg");
  if (sunSvg) {
    function sunPickFromEvent(e) {
      const rect = sunSvg.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = clientX - rect.left - 35; // center of 70px svg
      const dy = clientY - rect.top - 35;
      return {
        azimuth: Math.round(((Math.atan2(dx,-dy)*180/Math.PI)+360)%360),
        altitude: Math.round((Math.min(31.5, Math.sqrt(dx*dx+dy*dy))/31.5)*90),
      };
    }
    function moveSunDot(az, alt) {
      const r2 = (alt/90)*36;
      const azRad = az*Math.PI/180;
      const cx = (40+r2*Math.sin(azRad)).toFixed(1);
      const cy = (40-r2*Math.cos(azRad)).toFixed(1);
      const dot = container.querySelector("#surf-sun-dot"); if (dot) { dot.setAttribute("cx",cx); dot.setAttribute("cy",cy); }
      const glow = container.querySelector("#surf-sun-glow"); if (glow) { glow.setAttribute("cx",cx); glow.setAttribute("cy",cy); }
      const lbl = container.querySelector("#surf-sun-label"); if (lbl) lbl.textContent = `Az ${az}° · Alt ${alt}°`;
    }
    function startSunDrag(e) {
      e.preventDefault();
      const { azimuth, altitude } = sunPickFromEvent(e);
      st.hillshadeAzimuth = azimuth; st.hillshadeAltitude = altitude; moveSunDot(azimuth, altitude);
      function onMove(ev) {
        if (ev.touches) ev.preventDefault();
        const { azimuth:az, altitude:alt } = sunPickFromEvent(ev);
        st.hillshadeAzimuth = az; st.hillshadeAltitude = alt; moveSunDot(az, alt);
      }
      function onUp() {
        document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp);
        document.removeEventListener("touchmove", onMove); document.removeEventListener("touchend", onUp);
        const az = st.hillshadeAzimuth, alt = st.hillshadeAltitude;
        applyToAllSelected(s => { s.settings.hillshadeAzimuth = az; s.settings.hillshadeAltitude = alt; });
        for (const id of selectedIds) {
          if (!visibleIds.has(id)) continue;
          const sRt = surfaceRuntimes.get(id);
          const s = catalog.find(c => c.id === id);
          if (!sRt || !s?.settings.hillshadeOverlay) continue;
          if (sRt.hillshadeLayer) { try { sRt.hillshadeLayer.remove(); } catch {} sRt.hillshadeLayer = null; }
          sRt.hillshadeLayer = createHillshadeLayer(s, sRt, app.map.instance, sRt.colormapId ?? null);
          bindTileProgress(sRt, sRt.hillshadeLayer);
        }
        saveCatalog();
      }
      document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp);
      document.addEventListener("touchmove", onMove, { passive:false }); document.addEventListener("touchend", onUp);
    }
    sunSvg.addEventListener("mousedown", startSunDrag);
    sunSvg.addEventListener("touchstart", startSunDrag, { passive:false });
  }

  // Gamma
  container.querySelector("#surf-gamma")?.addEventListener("input", e => {
    st.tileGamma = Number(e.target.value)/100;
    const lbl = container.querySelector("#surf-gamma-val"); if (lbl) lbl.textContent = st.tileGamma.toFixed(2);
    if (rt?.paneEl) rt.paneEl.style.filter = buildPaneFilter(sel);
  });
  container.querySelector("#surf-gamma")?.addEventListener("change", e => {
    const gamma = Number(e.target.value)/100;
    applyToAllSelected(s => { s.settings.tileGamma = gamma; const sRt = surfaceRuntimes.get(s.id); if (sRt?.paneEl) sRt.paneEl.style.filter = buildPaneFilter(s); });
    saveCatalog();
  });

  // Saturation
  container.querySelector("#surf-saturation")?.addEventListener("input", e => {
    st.tileSaturation = Number(e.target.value)/100;
    const lbl = container.querySelector("#surf-saturation-val"); if (lbl) lbl.textContent = st.tileSaturation.toFixed(2);
    if (rt?.paneEl) rt.paneEl.style.filter = buildPaneFilter(sel);
  });
  container.querySelector("#surf-saturation")?.addEventListener("change", e => {
    const sat = Number(e.target.value)/100;
    applyToAllSelected(s => { s.settings.tileSaturation = sat; const sRt = surfaceRuntimes.get(s.id); if (sRt?.paneEl) sRt.paneEl.style.filter = buildPaneFilter(s); });
    saveCatalog();
  });

  // Contrast
  container.querySelector("#surf-contrast")?.addEventListener("input", e => {
    st.tileContrast = Number(e.target.value)/100;
    const lbl = container.querySelector("#surf-contrast-val"); if (lbl) lbl.textContent = st.tileContrast.toFixed(2);
    if (rt?.paneEl) rt.paneEl.style.filter = buildPaneFilter(sel);
  });
  container.querySelector("#surf-contrast")?.addEventListener("change", e => {
    const con = Number(e.target.value)/100;
    applyToAllSelected(s => { s.settings.tileContrast = con; const sRt = surfaceRuntimes.get(s.id); if (sRt?.paneEl) sRt.paneEl.style.filter = buildPaneFilter(s); });
    saveCatalog();
  });

  // Z-factor (hillshade vertical exaggeration) — rebuilds hillshade tiles on change
  container.querySelector("#surf-zfactor")?.addEventListener("input", e => {
    st.tileZFactor = Number(e.target.value)/10;
    const lbl = container.querySelector("#surf-zfactor-val"); if (lbl) lbl.textContent = st.tileZFactor.toFixed(1)+"×";
  });
  container.querySelector("#surf-zfactor")?.addEventListener("change", e => {
    const zf = Number(e.target.value)/10;
    applyToAllSelected(s => {
      s.settings.tileZFactor = zf;
      const sRt = surfaceRuntimes.get(s.id);
      if (sRt?.hillshadeLayer && s.settings.hillshadeOverlay && visibleIds.has(s.id)) {
        try { sRt.hillshadeLayer.remove(); } catch {}
        sRt.hillshadeLayer = createHillshadeLayer(s, sRt, app.map.instance, sRt.colormapId ?? null);
        bindTileProgress(sRt, sRt.hillshadeLayer);
      }
    });
    saveCatalog();
  });

  // Reset adjustments
  container.querySelector("#surf-adj-reset")?.addEventListener("click", () => {
    applyToAllSelected(s => {
      s.settings.tileZFactor    = 1.0;
      s.settings.tileGamma      = 1.0;
      s.settings.tileSaturation = 1.0;
      s.settings.tileContrast   = 1.0;
      const sRt = surfaceRuntimes.get(s.id);
      if (sRt?.paneEl) sRt.paneEl.style.filter = "";
      if (sRt?.hillshadeLayer && s.settings.hillshadeOverlay && visibleIds.has(s.id)) {
        try { sRt.hillshadeLayer.remove(); } catch {}
        sRt.hillshadeLayer = createHillshadeLayer(s, sRt, app.map.instance, sRt.colormapId ?? null);
        bindTileProgress(sRt, sRt.hillshadeLayer);
      }
    });
    saveCatalog(); render();
  });
}

// ── Tool definition ───────────────────────────────────────────────────────────

function onMarinaChanged(e) {
  const code = String(e.detail?.marinaId || "").trim().toUpperCase();
  if (code === activeMarinaCode) return;
  activeMarinaCode = code;
  catalog = [];
  selectedId = null;
  selectedIds.clear();
  render();
  loadCatalog();
}

export const surfacesTool = {
  id: "surfaces",
  name: "Surfaces",
  init(appContext) {
    app = appContext;
    document.addEventListener("hydrotools:marina-changed", onMarinaChanged);
  },
  destroy() {
    document.removeEventListener("hydrotools:marina-changed", onMarinaChanged);
    for (const id of [...buildPolls.keys()]) stopBuildPoll(id);
    for (const [id] of surfaceRuntimes) hideSurface(id);
    surfaceRuntimes.clear();
    visibleIds.clear();
    if (closeCmDropdown) { document.removeEventListener("mousedown", closeCmDropdown); closeCmDropdown = null; }
    cmPickerOpen = false;
    catalog = [];
    activeMarinaCode = "";
    selectedId = null;
    selectedIds.clear();
    container = null;
    app = null;
  },
  renderPanel(_appContext, panelEl) {
    container = panelEl;
    render();
  },
};
